import { api } from './client';

export type AffiliateTier = 'NONE' | 'BRONZE' | 'SILVER' | 'FOUNDERS_CIRCLE';

export interface TierInfo {
  current: AffiliateTier;
  label: string;
  badge: string;
  achievedAt: string | null;
  next: AffiliateTier | null;
  nextLabel: string | null;
  needs: null | {
    personalSpendUsdt?: number;
    directTeamVolumeUsdt?: number;
  };
  rates: {
    // Direct (L1) — what you earn on referrals you brought yourself
    usdtPercent: number;
    aimlPercent: number;
    // Indirect (L2) — what you earn on your referrals' referrals (passive)
    l2UsdtPercent: number;
    l2AimlPercent: number;
    /** @deprecated equals Founders Circle's L2 USDT rate. Use l2UsdtPercent. */
    leadershipOverridePercent: number;
  };
}

export interface MyPayout {
  id: string;
  usdtAmount: number;
  txHash: string;
  destinationAddress: string;
  commissionCount: number;
  createdAt: string;
  confirmedAt: string | null;
  notes: string | null;
}

export interface ReferralSummary {
  referralCode: string;
  referralUrl: string | null;
  tier: TierInfo;
  spend: {
    personalUsdt: number;
    /** @deprecated kept for backward-compat; same value as teamVolumeUsdt */
    directTeamVolumeUsdt: number;
    teamVolumeUsdt: number;
  };
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  stats: {
    totalClicks: number;
    totalSignups: number;
    qualifyingReferrals: number;
  };
  earnings: {
    totalCommissionEarnedUsdt: number;
    totalCommissionPaidUsdt: number;
    payableUsdt: number;
    /** @deprecated kept for backward-compat; same value as totalIndirectUsdt */
    totalLeadershipOverrideEarnedUsdt: number;
    /** Total L2 (indirect) USDT earnings across all tiers — 2% for Bronze/Silver, 3% for Founders */
    totalIndirectUsdt: number;
  };
  incentives?: {
    /** Total AIML reward tokens earned (bonus program), excluding voided */
    aimlEarned: number;
    /** Total AIML tokens purchased through the linked wallet */
    aimlPurchased: number;
  };

  // Legacy fields (backward compatibility for any older code paths)
  referrerEligible: boolean;
  referrerEligibleAt: string | null;
  totalPurchasedUsdt: number;
  amountNeededForEligibility: number;
}

export interface Referee {
  id: string;
  email: string;
  walletAddress: string | null;
  joinedAt: string;
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  totalPurchasedUsdt: number;
  qualifyingPurchases: number;
  youEarnedUsdt: number;
}

export const referralApi = {
  me: () => api<ReferralSummary>('/api/referral/me'),

  referees: () => api<{ referees: Referee[]; total: number }>('/api/referral/referees'),

  attribute: (data: { referralCode: string; txHash: string; walletAddress: string }) =>
    api<{ message: string }>('/api/referral/attribute', { method: 'POST', body: data }),

  click: (data: { referralCode: string; sessionId?: string }) =>
    api<void>('/api/referral/click', { method: 'POST', body: data }),

  payouts: () =>
    api<{ payouts: MyPayout[]; totalPaidUsdt: number }>('/api/referral/payouts'),
};